import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
    ActivatedRoute,
    Router,
    RouterModule,
    TitleStrategy,
} from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from 'app/auth/auth.service';
import { EncryptionService } from 'app/services/encryption.service';
import { InputGeneralComponent } from 'app/components/input-general/input-general.component';
import {
    CHANGE_LANGUAGE,
    getUtmSourceBRPURL,
} from '../../../../app/shared/utils';
import { GlobalToasterService } from 'app/services/global-toaster.service';
import { GlobalToasterComponent } from '../../../components/global-toaster/global-toaster.component';
import { ToastrService } from 'ngx-toastr';

export function noSpacesValidator(): ValidatorFn {
    return function (control: AbstractControl): ValidationErrors | null {
        const hasSpaces = (control.value as string).includes(' ');

        return hasSpaces ? { hasSpaces: true } : null;
    };
}

@Component({
    selector: 'app-sign-up',
    templateUrl: './sign-up.component.html',
    styleUrls: ['./sign-up.component.scss'],
    imports: [
        InputGeneralComponent,
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        TranslateModule,
        MatIconModule,
        MatProgressSpinnerModule,
        RouterModule,
        GlobalToasterComponent,
    ],
    standalone: true,
    styles: [
        `
            :host {
                width: 100%;
            }
        `,
    ],
})
export class SignUpComponent {
    signUpForm: FormGroup;
    submitted = false;
    patternMatch: any;
    pswdStrength: any = 0;
    pswdStrengthLabel: any = '';
    pswdStrengthColor: any = '';
    isCompanyNameRequired;
    isCompanyNameInvalid;
    isCompanyNameMinMax;
    isRequiredError;
    invalidEmailError;
    isEmailRequiredError;
    invalidPhoneNumberEntered;
    confirmPasswordInvalid;
    digitOnlyError;
    passwordMatchError;
    passwordMinMaxError;
    passwordUppercaseError;
    passwordLowercaseError;
    passwordDigitError;
    passwordSpecialCharacterError;
    phoneNoMinMaxError;
    usernameMinMaxError;
    usernameSpaceError;
    closeTostr: string;
    errMsg: string;
    refferal: string;
    // loading: boolean = false;
    selectedLanguage: any;
    JSON: JSON;
    calendarEventURL = '';
    brpSourceUrl = '';
    utmId = '';
    utmSource = '';
    getUtmId;
    getUtmSource;
    isAuthenticated: boolean;

    constructor(
        private authService: AuthService,
        private fb: FormBuilder,
        private router: Router,
        private translate: TranslateService,
        private activatedRoute: ActivatedRoute,
        private encryptionService: EncryptionService,
        public globalToasterSerive: GlobalToasterService,
        private toastr: ToastrService
    ) {
        this.JSON = JSON;
    }

    ngOnInit() {
        this.selectedLanguage = localStorage.getItem('language');
        this.signUpForm = this.fb.group(
            {
                username: [
                    '',
                    [
                        Validators.required,
                        noSpacesValidator(),
                        Validators.pattern(/^[A-Za-z0-9@._-]+$/),
                        Validators.minLength(3),
                        Validators.maxLength(50),
                    ],
                ],
                email: [
                    '',
                    [
                        Validators.required,
                        Validators.pattern(
                            '^[a-z0-9._-]+@[a-z0-9.-]+\\.[a-z]{2,4}$'
                        ),
                        Validators.minLength(6),
                        Validators.maxLength(50),
                    ],
                ],
                password: ['', [Validators.required]],
                confirmPassword: ['', Validators.required],
                companyName: [
                    '',
                    [Validators.minLength(1), Validators.maxLength(50)],
                ],
                phoneNumber: [
                    '',
                    [
                        Validators.required,
                        Validators.minLength(9),
                        Validators.maxLength(10),
                    ],
                ],
                referral: [
                    '',
                    [Validators.minLength(3), Validators.maxLength(50)],
                ],
                acceptTerms: [false, Validators.requiredTrue],
            },
            {
                validator: this.confirmPasswordValidation,
            }
        );

        this._translate();

        // assigning returnUrl flag for redirection
        this.activatedRoute.queryParams.subscribe((params) => {
            this.calendarEventURL = params.calendarEventURL;
            this.utmId = params.utm_id;
            this.utmSource = params.utm_source;
            this.brpSourceUrl = params?.brpSourceUrl;
            this.refferal = params?.ref;
        });

        if (this.refferal) {
            this.signUpForm.patchValue({ referral: this.refferal });
        }

        //To check if user is coming from reference/BRP URL
        if (this.utmId) {
            sessionStorage.setItem('utm_id', JSON.stringify(this.utmId));
        }
        //To check if user is coming from BRP URL
        if (this.utmSource) {
            sessionStorage.setItem(
                'utm_source',
                JSON.stringify(this.utmSource)
            );
        }

        this.getUtmId = JSON.parse(sessionStorage.getItem('utm_id'));
        this.getUtmSource = JSON.parse(sessionStorage.getItem('utm_source'));

        this.isAuthenticated =
            localStorage.getItem('isAuthenticated') == 'true' ? true : false;
        if (this.isAuthenticated) {
            const encryptionKey = atob(localStorage.getItem('cepn'));
            let profile = JSON.parse(
                atob(
                    this.encryptionService.decryptData(
                        localStorage.getItem('profile'),
                        encryptionKey
                    )
                )
            );

            if (this.utmSource && this.utmId == 'BRP') {
                //To check if user is coming from reference/BRP URL
                let brpSourceUrl = `/business-financing?utm_source=${this.utmSource}&utm_medium=Referral&utm_campaign=BRP&utm_id=BRP`;
                this.router.navigateByUrl(brpSourceUrl);
            } else if (profile.smsverify == 'Unverified') {
                this.router.navigate(['/profile']);
            } else {
                this.router.navigate(['/dashboard']);
            }
        }
    }

    private _translate() {
        this.translate.get('ERROR.IS_REQUIRED').subscribe((data: any) => {
            this.isRequiredError = data;
        });

        this.translate.get('ERROR.PATTERN_MATCH').subscribe((data: any) => {
            this.patternMatch = data;
        });

        this.translate
            .get('ERROR.INVALID_EMAIL_ENTERED')
            .subscribe((data: any) => {
                this.invalidEmailError = data;
            });

        this.translate.get('ERROR.EMAIL_REQUIRED').subscribe((data: any) => {
            this.isEmailRequiredError = data;
        });

        this.translate
            .get('ERROR.PASSWORD_MUST_MATCH')
            .subscribe((data: any) => {
                this.passwordMatchError = data;
            });

        this.translate
            .get('ERROR.PASSWORD_MIN_MAX_CHARACTERS')
            .subscribe((data: any) => {
                this.passwordMinMaxError = data;
            });

        this.translate
            .get('ERROR.PASSWORD_UPPERCASE')
            .subscribe((data: any) => {
                this.passwordUppercaseError = data;
            });

        this.translate
            .get('ERROR.PASSWORD_LOWERCASE')
            .subscribe((data: any) => {
                this.passwordLowercaseError = data;
            });

        this.translate.get('ERROR.PASSWORD_DIGIT').subscribe((data: any) => {
            this.passwordDigitError = data;
        });

        this.translate
            .get('ERROR.PASSWORD_SPECIAL_CHARACTER')
            .subscribe((data: any) => {
                this.passwordSpecialCharacterError = data;
            });

        this.translate.get('ERROR.DIGIT_ONLY').subscribe((data: any) => {
            this.digitOnlyError = data;
        });

        this.translate.get('ERROR.PHONE_NO').subscribe((data: any) => {
            this.phoneNoMinMaxError = data;
        });

        this.translate.get('ERROR.USERNAME').subscribe((data: any) => {
            this.usernameMinMaxError = data;
        });

        this.translate.get('ERROR.USERNAME_SPACES').subscribe((data: any) => {
            this.usernameSpaceError = data;
        });
        this.translate
            .get('ERROR.COMPANY_NAME_REQUIRED')
            .subscribe((data: any) => {
                this.isCompanyNameRequired = data;
            });
        this.translate
            .get('ERROR.COMPANY_NAME_INVALID')
            .subscribe((data: any) => {
                this.isCompanyNameInvalid = data;
            });
        this.translate
            .get('ERROR.COMPANY_NAME_MIN_MAX')
            .subscribe((data: any) => {
                this.isCompanyNameMinMax = data;
            });

        this.translate
            .get('ERROR.INVALID_PHONE_NUMBER_ENTERED')
            .subscribe((data: any) => {
                this.invalidPhoneNumberEntered = data;
            });

        this.translate
            .get('ERROR.INVALID_PASSWORD_ENTERED')
            .subscribe((data: any) => {
                this.confirmPasswordInvalid = data;
            });

        this.translate
            .get('ERROR.SOMETHING_WENT_WRONG')
            .subscribe((data: any) => {
                this.errMsg = data;
            });

        this.translate.get('COMMON.CLOSE').subscribe((data: any) => {
            this.closeTostr = data;
        });
    }

    get f() {
        return this.signUpForm.controls;
    }

    getErrors(input: any, inputName?: any): Array<any> {
        let errors = [];
        if (this.f[input].touched || this.submitted) {
            if (
                this.f[input].errors?.required &&
                input !== 'email' &&
                input !== 'phoneNumber' &&
                input != 'confirmPassword'
            ) {
                errors.push(inputName + ' ' + this.isRequiredError);
            }

            if (input == 'email') {
                if (this.f[input].errors?.email) {
                    errors.push(this.invalidEmailError);
                } else if (
                    this.f[input].value == '' ||
                    this.f[input].errors?.required
                ) {
                    errors.push(this.isEmailRequiredError);
                }
                if (
                    this.f[input].errors?.pattern ||
                    this.f[input].errors?.minlength ||
                    this.f[input].errors?.maxlength
                ) {
                    errors.push(inputName + ' ' + this.patternMatch);
                }
            }

            if (this.f[input].errors?.hasSpaces) {
                errors.push(inputName + ' ' + this.usernameSpaceError);
            }
            if (this.f[input].errors?.pattern && input !== 'email') {
                // Add input !== 'email' since email validation is on line 318.
                errors.push(inputName + ' ' + this.patternMatch);
            }

            if (
                input == 'password' &&
                !this.f[input].value.match(
                    /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{8,16}$/
                )
            ) {
                errors.push(
                    inputName + ' ' + this.passwordMinMaxError,
                    inputName + ' ' + this.passwordUppercaseError,
                    inputName + ' ' + this.passwordLowercaseError,
                    inputName + ' ' + this.passwordDigitError,
                    inputName + ' ' + this.passwordSpecialCharacterError
                );
            }
            if (this.f[input].errors?.MatchPassword) {
                errors.push(this.passwordMatchError);
            }
            if (
                this.f[input].errors?.minlength ||
                this.f[input].errors?.maxlength
            ) {
                if (input == 'username' || input == 'referral') {
                    errors.push(inputName + ' ' + this.usernameMinMaxError);
                }
                if (input == 'phoneNumber') {
                    errors.push(inputName + ' ' + this.phoneNoMinMaxError);
                }
                if (input == 'companyName') {
                    errors.push(inputName + ' ' + this.isCompanyNameMinMax);
                }
            }
            if (input == 'confirmPassword' && this.f[input].value == '') {
                errors.push(this.confirmPasswordInvalid);
            }
            if (
                input == 'phoneNumber' &&
                !this.f[input].value.match(/^[0-9]+$/)
            ) {
                errors.push(this.invalidPhoneNumberEntered);
            }
        }
        return errors;
    }

    checkPasswordStatus(event: any) {
        let password = event.target.value;

        if (
            password &&
            this.signUpForm.controls['password'].value.match(
                /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{8,16}$/
            )
        ) {
            if (password.length === 8) {
                this.pswdStrength = 1;
                this.pswdStrengthLabel = 'POOR';
                this.pswdStrengthColor = '#FF8819';
            } else if (password.length > 8 && password.length <= 11) {
                this.pswdStrength = 2;
                this.pswdStrengthLabel = 'FINE';
                this.pswdStrengthColor = '#FF8819';
            } else if (password.length > 11 && password.length <= 14) {
                this.pswdStrength = 3;
                this.pswdStrengthLabel = 'GOOD';
                this.pswdStrengthColor = '#4B6EEC';
            } else if (password.length > 14 && password.length <= 16) {
                this.pswdStrength = 4;
                this.pswdStrengthLabel = 'STRONG';
                this.pswdStrengthColor = '#4B6EEC';
            }
        } else {
            this.pswdStrength = 0;
            this.pswdStrengthLabel = '';
            this.pswdStrengthColor = '';
        }
    }

    confirmPasswordValidation(control: AbstractControl) {
        const password = control.get('password').value;
        const confirmPassword = control.get('confirmPassword').value;
        if (password && confirmPassword) {
            if (password !== confirmPassword) {
                control
                    .get('confirmPassword')
                    .setErrors({ MatchPassword: true });
            } else {
                control.get('confirmPassword').setErrors(null);
            }
        }
    }

    disablePaste(event: any) {
        event.preventDefault();
    }

    onAcceptTerms(event: any) {}

    onSubmitUserDetails() {
        this.submitted = true;
        const userDetails = {
            username: this.signUpForm.value.username,
            password: this.signUpForm.value.password,
            email: this.signUpForm.value.email,
            phoneNumber: '+60' + this.signUpForm.value.phoneNumber,
            details: { bizname: this.signUpForm.value.companyName },
            referral: this.signUpForm.value.referral,
        };

        if (this.getUtmSource) {
            userDetails['reference'] = this.utmSource;
        } else if (this.getUtmId) {
            userDetails['reference'] = this.utmId;
        }
        if (this.signUpForm.valid) {
            this.authService.userSignUp(userDetails).subscribe({
                next: () => {
                    let userData = {
                        username: this.signUpForm.value.username,
                        email: this.signUpForm.value.email,
                    };

                    const navigationExtras = {
                        state: { userDetails: userData },
                    };
                    if (this.calendarEventURL)
                        navigationExtras['queryParams'] = {
                            calendarEventURL: this.calendarEventURL,
                        };
                    else if (this.utmSource && this.utmId == 'BRP') {
                        navigationExtras['queryParams'] = {
                            brpSourceUrl: `/business-financing?utm_source=${this.utmSource}&utm_medium=Referral&utm_campaign=BRP&utm_id=BRP`,
                        };
                    } else if (this.brpSourceUrl) {
                        let utmSource = getUtmSourceBRPURL(this.brpSourceUrl);
                        navigationExtras['queryParams'] = {
                            brpSourceUrl: `/business-financing?utm_source=${utmSource}&utm_medium=Referral&utm_campaign=BRP&utm_id=BRP`,
                        };
                    }
                    this.router.navigate(['/resend-code'], navigationExtras);
                },
                error: (err) => {
                    let errorMsg = err?.error?.message;
                    let errReason = err?.error?.reason;

                    this.translate.get(errReason).subscribe((data: any) => {
                        if (data !== errReason) {
                            errorMsg = data;
                        } else {
                            errorMsg = this.errMsg;
                        }
                    });
                    this.toastr.error(errorMsg);
                },
            });
        }
    }

    onSignIn() {
        if (this.utmSource && this.utmId == 'BRP') {
            this.router.navigate(['/login'], {
                queryParams: {
                    brpSourceUrl: `/business-financing?utm_source=${this.utmSource}&utm_medium=Referral&utm_campaign=BRP&utm_id=BRP`,
                },
            });
        } else {
            this.router.navigate(['/login']);
        }
    }

    switchLanguage(language: string) {
        CHANGE_LANGUAGE(language, this.router.url, this.translate);
        this._translate();
        this.selectedLanguage = localStorage.getItem('language');
    }
}
